import React from 'react';

import { differenceWith, toPairs, isEqual } from 'lodash-es';
import Helmet from 'react-helmet';
import { FormattedMessage, injectIntl, InjectedIntl } from 'react-intl';
import { connect } from 'react-redux';

import Button from '@atlaskit/button/standard-button';
import { Grid, GridColumn } from '@atlaskit/page';
import UFOLabel from '@atlaskit/react-ufo/label';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import Tooltip from '@atlaskit/tooltip';

import { FocusedPageHeader } from 'src/components/accessibility';
import { PullRequestsTableStateVisibility } from 'src/components/pull-requests-table';
import { PULL_REQUEST_LIST_LOAD, MetricSender } from 'src/metrics';
import { getPullRequestList } from 'src/redux/pull-request-list';
import { Link } from 'src/router/components';
import Breadcrumbs from 'src/sections/repository/containers/breadcrumbs';
import { RepositoryPrivilege } from 'src/sections/repository/types';
import urls from 'src/sections/repository/urls';
import { checkUserAccess } from 'src/sections/repository/utils/check-user-access';
import { getUpToSmallBreakpointState } from 'src/selectors/global-selectors';
import { getRepositoryAccessLevel } from 'src/selectors/repository-selectors';
import { BucketState } from 'src/types/state';
import {
  FilterEventMap,
  publishScreenEvent,
  publishUiEvent,
} from 'src/utils/analytics/publish';

import * as styles from '../../../styles/page';
import Filter from '../containers/filter';
import PullRequestList from '../containers/list';
import Pagination from '../containers/pagination';
import { Filter as FilterType, PRFilterState } from '../types';

import EmptyState from './empty-state';
import i18n from './i18n';
import { PaginationContainer, Section } from './page.styled';

type Props = {
  isEmpty: boolean;
  intl: InjectedIntl;
  filter: FilterType;
  repositoryOwner: string;
  repositorySlug: string;
  page: number;
  getPaginationUrl: (page: number) => string;
  onFilterChanged: (filter: FilterType) => void;
  onLoadPage: () => void;
  onPageChanged: () => void;
  canonicalHref: string;
  isUpToSmallBreakpointActive: boolean;
  userLevel: RepositoryPrivilege | null | undefined;
  isLoadingPullRequests: boolean;
};

type State = {
  prFilterPrevState: PRFilterState[];
};

const UI_EVENT_TEMPLATE = {
  action: 'changed',
  actionSubject: 'option',
  source: 'pullRequestsScreen',
};

export class PullRequestsPage extends React.PureComponent<Props, State> {
  static defaultProps = {
    /* eslint react/default-props-match-prop-types: "warn" */
    repositoryOwner: '',
    repositorySlug: '',
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      prFilterPrevState: [],
    };
  }

  componentDidMount() {
    publishScreenEvent('pullRequestsScreen');
  }

  mapFilterToEventCreator: FilterEventMap<FilterType> = {
    search: () => ({
      ...UI_EVENT_TEMPLATE,
      actionSubject: 'input',
      actionSubjectId: 'pullRequestSearch',
    }),
    isReviewing: isReviewing => ({
      ...UI_EVENT_TEMPLATE,
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'imReviewingButton',
      attributes: {
        isReviewing,
      },
    }),
    uuid: () => ({
      ...UI_EVENT_TEMPLATE,
      actionSubjectId: 'pullRequestAuthorSelect',
    }),
    branch: () => ({
      ...UI_EVENT_TEMPLATE,
      actionSubjectId: 'pullRequestBranchSelect',
    }),
    prState: state => ({
      ...UI_EVENT_TEMPLATE,
      actionSubjectId: 'pullRequestStateSelect',
      attributes: {
        state,
      },
    }),
    sort: sort => ({
      ...UI_EVENT_TEMPLATE,
      actionSubjectId: 'sortBySelect',
      attributes: {
        sort,
      },
    }),
  };

  handleFilterChanged = (filter: FilterType) => {
    differenceWith(
      toPairs(filter),
      toPairs(this.props.filter),
      isEqual
    ).forEach(([filterKey, filterValue]) => {
      // @ts-ignore TODO: fix noImplicitAny error here
      const createEvent = this.mapFilterToEventCreator[filterKey];
      if (createEvent) {
        publishUiEvent(createEvent(filterValue));
      }
    });

    this.props.onFilterChanged(filter);
  };

  handlePageChanged = () => {
    this.props.onPageChanged();
  };

  renderPullRequestList() {
    const {
      repositoryOwner,
      repositorySlug,
      filter,
      onLoadPage,
      page,
      getPaginationUrl,
      userLevel,
      isLoadingPullRequests,
    } = this.props;

    const pullRequestsUrl = urls.ui.pullRequests(
      repositoryOwner,
      repositorySlug
    );
    const createPullRequestUrl = urls.ui.createPullRequest(
      repositoryOwner,
      repositorySlug
    );

    const userHasAccess = checkUserAccess({
      userLevel,
      requiredLevel: 'write',
    });
    const action = userHasAccess ? (
      <div data-qa="create-pull-request-button">
        <Button href={createPullRequestUrl} component={Link}>
          <FormattedMessage {...i18n.buttonCreatePullRequest} />
        </Button>
      </div>
    ) : (
      <Tooltip
        content={<FormattedMessage {...i18n.buttonCreatePullRequestDisabled} />}
      >
        <div data-qa="create-pull-request-button">
          <Button isDisabled>
            <FormattedMessage {...i18n.buttonCreatePullRequest} />
          </Button>
        </div>
      </Tooltip>
    );

    const repositoryFullSlug = `${repositoryOwner}/${repositorySlug}`;

    const isFilterApplied =
      !!filter.uuid ||
      !!filter.branch ||
      !!filter.search ||
      !!filter.isReviewing ||
      !(filter.prState.length === 1 && filter.prState[0] === 'OPEN');

    const selectedStates = filter.prState;
    const prevSelectedStates = this.state.prFilterPrevState;

    const showStateLozenge =
      ((selectedStates.includes('ALL') || selectedStates.length > 1) &&
        !isLoadingPullRequests) ||
      ((prevSelectedStates.includes('ALL') || prevSelectedStates.length > 1) &&
        isLoadingPullRequests);

    return (
      <>
        <FocusedPageHeader actions={action} breadcrumbs={<Breadcrumbs />}>
          <FormattedMessage {...i18n.pageTitle} />
        </FocusedPageHeader>
        <Section>
          <div>
            <UFOLabel name="filter">
              <styles.FilterContainer>
                <Filter
                  filter={filter}
                  onFilterChanged={(event: FilterType) => {
                    this.setState({ prFilterPrevState: filter.prState });
                    this.handleFilterChanged(event);
                  }}
                  repositoryFullSlug={repositoryFullSlug}
                />
              </styles.FilterContainer>
            </UFOLabel>
            <PullRequestList
              showStateLozenge={
                showStateLozenge
                  ? PullRequestsTableStateVisibility.All
                  : PullRequestsTableStateVisibility.None
              }
              pullRequestsUrl={pullRequestsUrl}
              createPullRequestUrl={createPullRequestUrl}
              isFilterApplied={isFilterApplied}
              onLoadPage={onLoadPage}
              renderPagination={() => (
                <PaginationContainer>
                  <Pagination
                    page={page}
                    getPaginationUrl={getPaginationUrl}
                    onPageChange={() => {
                      this.handlePageChanged();
                      this.setState({ prFilterPrevState: filter.prState });
                    }}
                  />
                </PaginationContainer>
              )}
            />
          </div>
        </Section>
      </>
    );
  }

  renderEmptyState = () => (
    <>
      <FocusedPageHeader breadcrumbs={<Breadcrumbs />}>
        <FormattedMessage {...i18n.pageTitle} />
      </FocusedPageHeader>
      <EmptyState />
    </>
  );

  render() {
    const {
      intl,
      repositoryOwner,
      repositorySlug,
      canonicalHref,
      isUpToSmallBreakpointActive,
    } = this.props;
    const canonicalLink = { rel: 'canonical', href: canonicalHref };

    if (!repositoryOwner || !repositorySlug) {
      return null;
    }

    const title = `${intl.formatMessage(i18n.pageTitle)}`;

    return (
      <Grid
        layout="fluid"
        spacing={isUpToSmallBreakpointActive ? 'cosy' : 'comfortable'}
      >
        <Helmet defer={false} title={title} link={[canonicalLink]} />
        <GridColumn>
          <UFOLoadHold
            name="pull-request-list"
            hold={this.props.isLoadingPullRequests}
          >
            {this.props.isEmpty
              ? this.renderEmptyState()
              : this.renderPullRequestList()}
          </UFOLoadHold>
        </GridColumn>
        <MetricSender metric={PULL_REQUEST_LIST_LOAD} />
      </Grid>
    );
  }
}

const mapStateToProps = (state: BucketState) => ({
  isUpToSmallBreakpointActive: getUpToSmallBreakpointState(state),
  userLevel: getRepositoryAccessLevel(state),
  isLoadingPullRequests: getPullRequestList(state).isLoading,
});

export default connect(mapStateToProps)(injectIntl(PullRequestsPage));
